import React, { useEffect, useRef, useState } from "react";
import { UserPackageBadge } from "../../components/userPackageBadge/userPackageBadge";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { ModalBody } from "../../components/modalCommon/modalBody";
import type { WordModalModel } from "../../type/word";
import "./addWordsModal.scss";
import { useTranslation } from "../../components/translation/translation";
import { TranslateButton } from "../../components/translation/translateButton";

const WordModal: React.FC<WordModalModel> = ({
  show,
  onHide,
  onSave,
  canAddWord,
  remainingExpressions,
  maxExpressions,
  packageName,
  langName,
  mode = "add",
  initialEng,
  initialPl,
}) => {
  const [eng, setEng] = useState("");
  const [pl, setPl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { translating, translateToPl, translateToTarget } = useTranslation();
  const targetLangName = langName || "English";
  const isEditMode = mode === "edit";

  const engInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show) {
      setEng(initialEng ?? "");
      setPl(initialPl ?? "");
    } else {
      setEng("");
      setPl("");
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleTranslateToPl = async () => {
    const result = await translateToPl(eng);
    if (result) setPl(result);
  };

  const handleTranslateToEn = async () => {
    const result = await translateToTarget(pl, targetLangName);
    if (result) setEng(result);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eng.trim() || !pl.trim() || (!isEditMode && !canAddWord)) return;

    setIsSubmitting(true);
    try {
      await onSave(eng.trim(), pl.trim());
      if (!isEditMode) {
        setEng("");
        setPl("");
        engInputRef.current?.focus();
      }
    } catch (err) {
      console.error("Error saving word:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal open={show} onClose={onHide} size="md">
      <ModalHeader title={isEditMode ? "Edit Expression" : "Add New Expression"} onClose={onHide} />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Selected language */}
          <div className="mb-3">
            <label className="form-label">
              {targetLangName}
              <TranslateButton
                onClick={handleTranslateToEn}
                disabled={!pl.trim() || isSubmitting}
                loading={translating === "to-target"}
                title={`Translate to ${targetLangName}`}
              />              
            </label>
            <input
              ref={engInputRef}
              type="text"
              className="form-control bg-dark text-light border-secondary"
              placeholder={`Enter ${targetLangName} word or phrase`}
              value={eng}
              onChange={(e) => setEng(e.target.value)}
              disabled={(!isEditMode && !canAddWord) || isSubmitting}
              autoFocus
            />
          </div>

          {/* Polish */}
          <div className="mb-3">
            <label className="form-label">
              Polish Translation
              <TranslateButton
                onClick={handleTranslateToPl}
                disabled={!eng.trim() || isSubmitting}
                loading={translating === "en-pl"}
                title="Translate to Polish"
              />
            </label>
            <input
              type="text"
              className="form-control bg-dark text-light border-secondary"
              placeholder="Enter Polish translation"
              value={pl}
              onChange={(e) => setPl(e.target.value)}
              disabled={(!isEditMode && !canAddWord) || isSubmitting}
            />
          </div>

          {/* Limits */}
          {!isEditMode && maxExpressions !== -1 && (
            <div className="d-flex align-items-center justify-content-center mb-3">
              <UserPackageBadge packageName={packageName} />
              <span
                className={`ms-3 ${
                  remainingExpressions === 0
                    ? "text-danger"
                    : "text-light-green"
                }`}
              >
                {remainingExpressions} out of {maxExpressions} items left
              </span>
            </div>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary w-50"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Close
          </button>

          <button
            type="submit"
            className="btn btn-success w-50"
            disabled={
              isSubmitting || !eng.trim() || !pl.trim() || (!isEditMode && !canAddWord)
            }
          >
            {isEditMode ? (isSubmitting ? "Saving…" : "Save") : (isSubmitting ? "Adding…" : "+ Add")}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default WordModal;